import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRequest, useSize } from 'ahooks';
import styles from './index.module.scss';
import Header from './components/Header';
import { cancelFullscreen, goFullscreen, setFitScale } from './utils';
import LeftBox from './components/LeftBox';
import CenterBox from './components/CenterBox';
import RightBox from './components/RightBox';
import {
  apiV2MicrogridOverviewGetNewEnergyExistsPost,
  V2MicrogridOverviewGetNewEnergyExistsPostResponse,
} from '@maxtropy/intelligent-dispath-apis-v2';

export interface ShareDataProps {
  microgridId?: number;
  setMicrogridId?: React.Dispatch<React.SetStateAction<number | undefined>>;
  isFullScreen?: boolean;
  dataExit?: V2MicrogridOverviewGetNewEnergyExistsPostResponse;
}

export const ShareDataContext = createContext<ShareDataProps>({});

const IndustrialGreenMicBigScreen = () => {
  const resizeRef = useRef<any>();
  const baseDivRef = useRef<HTMLDivElement>();
  const size = useSize(baseDivRef);
  const innerDivRef = useRef<HTMLDivElement>();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false); // 是否全屏

  const [microgridId, setMicrogridId] = useState<number>(); // bsaId

  // 判断新能源节点是否存在
  const { data: dataExit } = useRequest(
    async () => {
      const res = await apiV2MicrogridOverviewGetNewEnergyExistsPost({
        id: Number(microgridId),
      });
      return res;
    },
    {
      ready: !!microgridId,
      refreshDeps: [microgridId],
      pollingInterval: 1000 * 60,
    }
  );

  const handleResize = () => {
    if (size?.width && size?.width > 1920) {
      setFitScale(innerDivRef.current, size?.width, size?.height, isFullScreen);
    }
  };

  useEffect(() => {
    if (resizeRef.current) {
      window.removeEventListener('resize', handleResize);
    }
    resizeRef.current = window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [size?.width, isFullScreen]);

  // 小屏幕
  const isSmallScreen = useMemo(() => {
    return !!(size?.width && size.width <= 1600);
  }, [size?.width]);

  // 全屏
  const handleFullScreen = () => {
    setIsFullScreen(document.fullscreenElement !== null);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreen);
    return () => document.removeEventListener('fullscreenchange', handleFullScreen);
  }, []);

  const fullBtn = () => {
    if (baseDivRef.current) {
      isFullScreen ? cancelFullscreen() : goFullscreen(baseDivRef.current);
    }
  };

  return (
    <div
      className={styles.outer_wrap}
      id="full_screen_box"
      style={{
        overflowY: isSmallScreen ? 'auto' : 'hidden',
      }}
      ref={ref => {
        if (ref) {
          baseDivRef.current = ref;
        }
      }}
    >
      <div
        className={styles.inner}
        ref={ref => {
          if (ref) {
            innerDivRef.current = ref;
          }
        }}
        style={{ height: isSmallScreen ? 'unset' : '100%' }}
      >
        <ShareDataContext.Provider value={{ setMicrogridId, microgridId, isFullScreen, dataExit }}>
          {/* 上方header区域 */}
          <div className={styles.inner_top}>
            <Header fullBtn={fullBtn} isFullScreen={isFullScreen} />
          </div>
          {isSmallScreen ? (
            <>
              <div className={styles.small_center_box}>
                {/* 中间（总览与拓扑图） */}
                <CenterBox />
              </div>
              <div className={styles.small_bottom_box}>
                {/* 左侧（数据总览、运行功率曲线、新能源社会效益） */}
                <div className={styles.item}>
                  <LeftBox />
                </div>
                {/* 右侧（新能源发电趋势、储能充放电、收益数据） */}
                <div className={styles.item}>
                  <RightBox />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* 中间content区域 */}
              <div className={styles.inner_center}>
                <div className={styles.center_left_box}>
                  {/* 左侧（数据总览、运行功率曲线、新能源社会效益） */}
                  <LeftBox />
                </div>
                <div className={styles.center_center_box}>
                  {/* 中间（总览与拓扑图） */}
                  <CenterBox />
                </div>

                <div className={styles.center_left_box}>
                  {/* 右侧（新能源发电趋势、储能充放电、收益数据） */}
                  <RightBox />
                </div>
              </div>
            </>
          )}
        </ShareDataContext.Provider>
      </div>
    </div>
  );
};

export default IndustrialGreenMicBigScreen;
